import {
  Component,
  TemplateRef,
  ViewContainerRef,
  input,
  output,
  signal,
  viewChild,
  booleanAttribute,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  Overlay,
  OverlayRef,
  OverlayModule,
  PositionStrategy,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Popover component - reusable overlay panel for displaying content
 *
 * By default renders in top-right corner with header-height offset from top
 * and 40px from right edge of max-width (1920px).
 *
 * @example
 * <!-- With template -->
 * <app-popover [contentTemplate]="menuTpl">
 *   <button popoverTrigger>Open</button>
 * </app-popover>
 *
 * <ng-template #menuTpl let-close="close">
 *   <app-user-menu (logout)="onLogout(); close()"></app-user-menu>
 * </ng-template>
 *
 * @example
 * <!-- With direct content -->
 * <app-popover>
 *   <button popoverTrigger>Open</button>
 *   <ng-container popoverContent>
 *     <div>Content here</div>
 *   </ng-container>
 * </app-popover>
 */
@Component({
  selector: 'app-popover',
  imports: [CommonModule, OverlayModule, MatIconModule],
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
})
export class PopoverComponent implements OnDestroy {
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _scrollStrategyOptions = inject(ScrollStrategyOptions);

  /** Internal wrapper template reference */
  private readonly _contentWrapper = viewChild<TemplateRef<unknown>>('popoverContentWrapper');

  /**
   * Template to render inside popover.
   * Template context includes { close: () => void } for closing popover from inside.
   */
  readonly contentTemplate = input<TemplateRef<{ close: () => void }>>();

  /**
   * Whether to show backdrop (clicking backdrop closes popover)
   */
  readonly hasBackdrop = input(true, { transform: booleanAttribute });

  /**
   * Additional CSS class(es) for the popover panel
   */
  readonly panelClass = input<string | string[]>('');

  /**
   * Custom width for the popover panel. Default is 268px.
   */
  readonly width = input<string>('268px');

  /**
   * Whether to show close button in top-right corner. Default is true.
   */
  readonly showCloseButton = input(true, { transform: booleanAttribute });

  /**
   * Emits when popover is opened
   */
  readonly opened = output<void>();

  /**
   * Emits when popover is closed
   */
  readonly closed = output<void>();

  /** Whether popover is currently open */
  protected readonly isOpen = signal(false);

  /** Close function bound to this instance (for template context) */
  protected readonly closeFn = (): void => this.close();

  private _overlayRef: OverlayRef | null = null;
  private _portal: TemplatePortal<unknown> | null = null;

  ngOnDestroy(): void {
    this._disposeOverlay();
  }

  /**
   * Toggle popover open/close state
   */
  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the popover
   */
  open(): void {
    if (this.isOpen()) return;

    this._createOverlay();
    this.isOpen.set(true);
    this.opened.emit();
  }

  /**
   * Close the popover
   */
  close(): void {
    if (!this.isOpen()) return;

    this._disposeOverlay();
    this.isOpen.set(false);
    this.closed.emit();
  }

  private _createOverlay(): void {
    const positionStrategy = this._getPositionStrategy();
    const scrollStrategy = this._scrollStrategyOptions.block();

    const panelClasses = ['popover-panel'];
    const customClasses = this.panelClass();
    if (customClasses) {
      if (Array.isArray(customClasses)) {
        panelClasses.push(...customClasses);
      } else {
        panelClasses.push(customClasses);
      }
    }

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: this.hasBackdrop(),
      backdropClass: 'popover-backdrop',
      panelClass: panelClasses,
      width: this.width(),
    });

    this._overlayRef.backdropClick().subscribe(() => this.close());

    this._overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.close();
      }
    });

    const wrapperTemplate = this._contentWrapper();
    if (wrapperTemplate) {
      this._portal = new TemplatePortal(wrapperTemplate, this._viewContainerRef);
      this._overlayRef.attach(this._portal);
    }
  }

  private _getPositionStrategy(): PositionStrategy {
    const styles = getComputedStyle(document.documentElement);
    const headerHeight = parseInt(styles.getPropertyValue('--header-height'), 10) || 68;
    const additionalSpaceFromTop = 8;
    const maxWidth = parseInt(styles.getPropertyValue('--app-max-width'), 10) || 1920;
    const rightOffset = 40;

    const viewportWidth = window.innerWidth;
    const rightPosition =
      viewportWidth > maxWidth ? (viewportWidth - maxWidth) / 2 + rightOffset : rightOffset;

    return this._overlay
      .position()
      .global()
      .top(`${headerHeight + additionalSpaceFromTop}px`)
      .right(`${rightPosition}px`);
  }

  private _disposeOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
    this._portal = null;
  }
}
